import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig
} from 'axios';
import { ref } from 'vue';
import { useInternetConnection } from './useInternetConnection';
import Swal from 'sweetalert2';
import {
  normalizeAuthPayload,
  type RawUserPayload
} from '@/services/authNormalizer';
import { setAuthState, clearAuthState, getAccessToken, getRefreshToken } from '@/services/tokenStorage';

interface AuthRequestConfigBase {
  _retry?: boolean;
  _skipAuthRefresh?: boolean;
}

interface ExtendedAxiosRequestConfig
  extends InternalAxiosRequestConfig,
  AuthRequestConfigBase { }

interface AuthAxiosRequestConfig extends AxiosRequestConfig, AuthRequestConfigBase { }

let isLoggingOut = false;
const initialNormalized = normalizeAuthPayload(undefined, getAccessToken(), getRefreshToken());
const user = ref(initialNormalized?.user ?? null);
const token = ref<string | null>(initialNormalized?.accessToken ?? getAccessToken());

const axiosInstance: AxiosInstance = axios.create({
  //baseURL: "https://test-api.sertelvida.com.tr",
  baseURL: import.meta.env.VITE_API_BASE_URL_LOCAL || '/api',
  timeout: 600000, // 10 dakika
  headers: {
    'Content-Type': 'application/json'
  }
});

axiosInstance.interceptors.request.use((config: ExtendedAxiosRequestConfig) => {
  const accessToken = getAccessToken();
  if (accessToken && !config._skipAuthRefresh) {
    if (!config.headers) {
      config.headers = {} as AxiosRequestHeaders;
    }

    const headers = config.headers as AxiosRequestHeaders & Record<string, string>;
    if (!headers.Authorization) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
  }

  return config;
});

let refreshPromise: Promise<string> | null = null;

const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('Missing refresh token');
  }

  const refreshRequestConfig: ExtendedAxiosRequestConfig = {
    _skipAuthRefresh: true,
    headers: {} as AxiosRequestHeaders
  };

  const response = await axiosInstance.post<RawUserPayload>(
    '/users/0.0.1/refresh-token',
    { refreshToken },
    refreshRequestConfig
  );

  const normalized = normalizeAuthPayload(response.data, undefined, refreshToken);
  if (!normalized) {
    throw new Error('Invalid refresh response');
  }

  setAuthState({
    accessToken: normalized.accessToken,
    refreshToken: normalized.refreshToken
  });

  user.value = normalized.user;
  token.value = normalized.accessToken;

  return normalized.accessToken;
};

const promptReLogin = async () => {
  if (isLoggingOut) {
    return;
  }

  isLoggingOut = true;
  clearAuthState();
  user.value = null;
  token.value = null;

  await Swal.fire({
    icon: 'warning',
    title: 'Oturum Süresi Doldu',
    text: 'Oturumunuzun süresi doldu. Lütfen tekrar giriş yapın.',
    showConfirmButton: true,
    confirmButtonText: 'Giriş Yap'
  });

  window.location.href = '/';

  setTimeout(() => {
    isLoggingOut = false;
  }, 3000);
};

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig | undefined;

    if (originalRequest?._skipAuthRefresh) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        await promptReLogin();
        return Promise.reject(error);
      }

      if (originalRequest?._retry) {
        return Promise.reject(error);
      }

      try {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken();
        }

        const newAccessToken = await refreshPromise;

        originalRequest!._retry = true;
        // @ts-expect-error - headers is not typed
        originalRequest!.headers = {
          ...(originalRequest!.headers || {}),
          Authorization: `Bearer ${newAccessToken}`
        };

        return axiosInstance(originalRequest!);
      } catch (refreshError) {
        const refreshStatus = (refreshError as AxiosError)?.response?.status;
        if (
          refreshStatus === 401 ||
          refreshStatus === 403 ||
          (refreshError instanceof Error && refreshError.message === 'Invalid refresh response')
        ) {
          await promptReLogin();
        }
        return Promise.reject(refreshError);
      } finally {
        refreshPromise = null;
      }
    }

    return Promise.reject(error);
  }
);

export const logout = async () => {
  try {
    isLoggingOut = true;
    user.value = null;
    token.value = null;
    clearAuthState();
    window.location.href = '/';
  } catch (err) {
    console.error(`Error on logout`, err)
  } finally {
    setTimeout(() => {
      isLoggingOut = false;
    }, 3000);
  }
};

export { axiosInstance };

export function useAxios() {
  const loading = ref<boolean>(false);
  const error = ref<AxiosError | null>(null);
  const { isOnline } = useInternetConnection();

  const checkInternetConnection = () => {
    if (!isOnline.value) {
      throw new Error('İnternet bağlantısı yok. Lütfen bağlantınızı kontrol edin.');
    }
  };

  const get = async <T>(url: string, config: AuthAxiosRequestConfig = {}): Promise<T> => {
    loading.value = true;
    error.value = null;
    try {
      checkInternetConnection();
      const response: AxiosResponse<T> = await axiosInstance.get<T>(url, config);
      return response.data;
    } catch (err) {
      error.value = err as AxiosError;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const patch = async <T>(url: string, data: any, config: AuthAxiosRequestConfig = {}): Promise<T> => {
    loading.value = true;
    error.value = null;
    try {
      checkInternetConnection();
      const response: AxiosResponse<T> = await axiosInstance.patch<T>(url, data, config);
      return response.data;
    } catch (err) {
      error.value = err as AxiosError;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const post = async <T>(url: string, data: any, config: AuthAxiosRequestConfig = {}): Promise<T> => {
    loading.value = true;
    error.value = null;
    try {
      checkInternetConnection();
      const response: AxiosResponse<T> = await axiosInstance.post<T>(url, data, config);
      return response.data;
    } catch (err) {
      error.value = err as AxiosError;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const put = async <T>(url: string, data: any, config: AuthAxiosRequestConfig = {}): Promise<T> => {
    loading.value = true;
    error.value = null;
    try {
      checkInternetConnection();
      const response: AxiosResponse<T> = await axiosInstance.put<T>(url, data, config);
      return response.data;
    } catch (err) {
      error.value = err as AxiosError;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const del = async <T>(url: string, config: AuthAxiosRequestConfig = {}): Promise<T> => {
    loading.value = true;
    error.value = null;
    try {
      checkInternetConnection();
      const response: AxiosResponse<T> = await axiosInstance.delete<T>(url, config);
      return response.data;
    } catch (err) {
      error.value = err as AxiosError;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    get,
    post,
    del,
    loading,
    error,
    patch,
    put,
    logout
  };
}
